import { CalendarDate, isSameDay } from '@internationalized/date'
import React from 'react'
import { mergeProps, useCalendarCell } from 'react-aria'
import { CalendarState, RangeCalendarState } from 'react-stately'
import styled, { css } from 'styled-components'
import { Text } from '../../../../core-components'

type Props = {
  'data-test-id'?: string
  date: CalendarDate
  state: CalendarState | RangeCalendarState
  isEndWeek: boolean
  isStartWeek: boolean
  variant: 'default' | 'today'
}

export const DateItem = ({
  state,
  date,
  variant,
  'data-test-id': dataTestId,
  isEndWeek,
  isStartWeek,
}: Props) => {
  const ref = React.useRef(null)
  const { cellProps, buttonProps, isSelected, isOutsideVisibleRange, formattedDate } =
    useCalendarCell({ date }, state, ref)

  const isSelectionStart =
    'highlightedRange' in state && state.highlightedRange
      ? isSameDay(date, state.highlightedRange.start)
      : isSelected
  const isSelectionEnd =
    'highlightedRange' in state && state.highlightedRange
      ? isSameDay(date, state.highlightedRange.end)
      : isSelected
  return (
    <td {...cellProps}>
      <StyledButton
        data-test-id={dataTestId}
        isSelected={isSelected}
        isStart={isSelectionStart}
        isEnd={isSelectionEnd}
        isEndWeek={isEndWeek}
        isStartWeek={isStartWeek}
        {...mergeProps(buttonProps)}
        ref={ref}
        hidden={isOutsideVisibleRange}
      >
        <Text textStyle='form-value' color={getColor(variant, isSelectionStart, isSelectionEnd)}>
          {formattedDate}
        </Text>
      </StyledButton>
    </td>
  )
}

const StyledButton = styled.button<{
  isEnd: boolean
  isSelected: boolean
  isStart: boolean
  isEndWeek: boolean
  isStartWeek: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  background: none;
  width: ${({ theme }) => theme.space[8]};
  height: ${({ theme }) => theme.space[8]};
  border-radius: ${({ theme }) => theme.radii[12]};
  :hover {
    background: ${({ theme }) => theme.colors['bg/list-item-hovered']};
  }
  &[aria-disabled] {
    cursor: not-allowed;
  }
  &[aria-disabled] ${Text} {
    color: ${({ theme }) => theme.colors['fg/disabled']};
  }
  :focus-visible {
    outline: none;
    background: ${({ theme }) => theme.colors['bg/list-item-hovered']};
    box-shadow: ${({ theme }) => theme.shadows['border/focus-indicator']};
  }

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      border-radius: ${theme.radii['none']};
      && {
        background: ${({ theme }) => theme.colors['bg/major']};
      }
    `}
  ${({ isStart, theme }) =>
    isStart &&
    css`
      && {
        background: ${theme.colors['bg/select-item-major-checked']};
      }
    `}
  
    ${({ isEnd, theme }) =>
    isEnd &&
    css`
      && {
        background: ${theme.colors['bg/select-item-major-checked']};
      }
    `}

    ${({ isEndWeek, isEnd, theme }) =>
    (isEndWeek || isEnd) &&
    css`
      border-bottom-right-radius: ${theme.radii[12]};
      border-top-right-radius: ${theme.radii[12]};
    `}

    ${({ isStartWeek, isStart, theme }) =>
    (isStartWeek || isStart) &&
    css`
      border-bottom-left-radius: ${theme.radii[12]};
      border-top-left-radius: ${theme.radii[12]};
    `}

  
  &[hidden] {
    visibility: hidden;
  }
`

const getColor = (
  variant: Props['variant'],
  isSelectionStart: boolean,
  isSelectionEnd: boolean
) => {
  if (isSelectionStart || isSelectionEnd) {
    return 'fg/major-on-color'
  }
  if (variant === 'today') {
    return 'fg/caution'
  }
  return 'fg/major'
}
